import { useMemo } from 'react';
import DeribitIcon from '../../../assets/icons/deribit-icon.svg';
import OkxIcon from '../../../assets/icons/okx-icon.svg';
import { getPreferredTokenIconSrc } from '../../../utils/token-icon-sources';
import { IPortfolioPerformanceAccounts } from '../../../providers/portfolio-performance';

type AccountGroup = {
  exchange: string;
  icon: string;
  items: IPortfolioPerformanceAccounts[];
};

const getGroupIcon = (exchange: string): string => {
  if (exchange === 'deribit') return DeribitIcon;
  if (exchange === 'reference') return getPreferredTokenIconSrc('BTC');
  return OkxIcon;
};

// Reference strategies (buy-and-hold baselines across the full perps
// universe — see use-reference-strategy-tokens.ts) always appear as their
// own selectable "Reference" group, regardless of whether the user has any
// real credentialed accounts — including for clients tracking no real
// accounts at all, seeing how a strategy would have compared to just
// holding the underlying is useful on its own. `referenceAccounts` is
// passed in (rather than imported as a static list) since the token
// universe is now fetched live, not fixed.
export const useAccountGroups = (
  credentials: IPortfolioPerformanceAccounts[],
  referenceAccounts: IPortfolioPerformanceAccounts[],
): AccountGroup[] => {
  return useMemo(() => {
    const map = new Map<string, AccountGroup>();
    const allItems = [...credentials, ...referenceAccounts];

    allItems.forEach((acct) => {
      const { exchange } = acct;

      if (!map.has(exchange)) {
        map.set(exchange, {
          exchange,
          icon: getGroupIcon(exchange),
          items: [],
        });
      }
      map.get(exchange)!.items.push(acct);
    });

    return Array.from(map.values()).sort((a, b) => a.exchange.localeCompare(b.exchange));
  }, [credentials, referenceAccounts]);
};
